import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageTemplate from '../components/LegalPageTemplate';

const SIDEBAR_NAV = [
  {
    title: 'Terms of Use',
    links: [
      { href: '#terms-acceptance', label: '1. Acceptance' },
      { href: '#terms-use', label: '2. Use of website' },
      { href: '#terms-medical', label: '3. Medical disclaimer' },
      { href: '#terms-ip', label: '4. Intellectual property' },
      { href: '#terms-liability', label: '5. Limitation of liability' },
      { href: '#terms-links', label: '6. Links to other sites' },
      { href: '#terms-changes', label: '7. Changes' },
      { href: '#terms-governing', label: '8. Governing law' },
      { href: '#terms-contact', label: '9. Contact' },
    ],
  },
  {
    title: '1-to-1 Exercise Coaching',
    links: [
      { href: '#coaching-purchasing', label: '1. Purchasing a programme' },
      { href: '#coaching-cancellation', label: '2. Cancellation of sessions' },
      { href: '#coaching-lateness', label: '3. Lateness' },
      { href: '#coaching-expiry', label: '4. Expiry and extensions' },
      { href: '#coaching-ep', label: '5. Your Exercise Physiologist' },
      { href: '#coaching-refunds', label: '6. Refunds' },
      { href: '#coaching-privacy', label: '7. Our commitment to your privacy' },
      { href: '#coaching-health', label: '8. Health and Safety' },
      { href: '#coaching-conduct', label: '9. Conduct' },
      { href: '#coaching-ip', label: '10. Intellectual Property' },
      { href: '#coaching-liability', label: '11. Limitation of Liability' },
      { href: '#coaching-changes', label: '12. Changes to Terms of Service' },
      { href: '#coaching-governing', label: '13. Governing Law' },
      { href: '#coaching-contact', label: '14. Contact Us' },
      { href: '#coaching-informed-consent', label: 'Informed consent' },
      { href: '#coaching-marketing', label: 'Marketing communications' },
    ],
  },
];

const TermsPage: React.FC = () => (
  <LegalPageTemplate
    title="Terms of Use"
    description="Terms and conditions for use of the PH Therapy website."
    metaDescription="Terms and conditions for use of the PH Therapy website. Disclaimers and applicable conditions."
    canonicalPath="/terms"
    lastUpdated="February 2026"
    mainId="terms-page"
    sidebarNav={SIDEBAR_NAV}
  >
    <section id="terms-acceptance">
      <h2>1. Acceptance</h2>
      <p>By using this website, you agree to these terms of use. If you do not agree, please do not use the site.</p>
    </section>

    <section id="terms-use">
      <h2>2. Use of website</h2>
      <p>You may use this website for lawful purposes only. You must not use it to transmit harmful content, attempt unauthorised access, or interfere with the site&apos;s operation.</p>
    </section>

    <section id="terms-medical">
      <h2>3. Medical disclaimer</h2>
      <p>This website provides general information only. It is not a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of a qualified healthcare professional before starting any exercise programme or making changes to your health regime. Never disregard professional medical advice because of something you have read on this site.</p>
    </section>

    <section id="terms-ip">
      <h2>4. Intellectual property</h2>
      <p>All content on this website (text, images, logos, design) is owned by PH Therapy or its licensors. You may not copy, reproduce, or distribute any content without our prior written consent, except for personal non-commercial use.</p>
    </section>

    <section id="terms-liability">
      <h2>5. Limitation of liability</h2>
      <p>PH Therapy is not liable for any direct, indirect, or consequential loss arising from your use of this website or reliance on its content. We do not guarantee that the site will be uninterrupted, error-free, or free of viruses. Use of the site is at your own risk.</p>
    </section>

    <section id="terms-links">
      <h2>6. Links to other sites</h2>
      <p>This website may contain links to external sites. We are not responsible for the content or privacy practices of those sites.</p>
    </section>

    <section id="terms-changes">
      <h2>7. Changes</h2>
      <p>We may update these terms from time to time. The &quot;Last updated&quot; date at the top indicates when changes were last made. Continued use of the site after changes constitutes acceptance.</p>
    </section>

    <section id="terms-governing">
      <h2>8. Governing law</h2>
      <p>These terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
    </section>

    <section id="terms-contact">
      <h2>9. Contact</h2>
      <p>For questions about these terms, contact us via the <Link to="/contact">Contact page</Link>.</p>
    </section>

    <hr className="terms-divider" />

    <h2 className="terms-subpage-title">1-to-1 Exercise Coaching Terms of Service</h2>
    <p className="content-page-lead">
      Terms of Service for in-person and online exercise coaching. By signing up for and attending our exercise sessions, you agree to be bound by these terms.
    </p>

    <section id="coaching-purchasing">
      <h2>1. Purchasing a programme</h2>
      <p>Payment is to be made before the start of the programme. All payments are to be made by bank transfer to Performance &amp; Health Therapy Ltd.</p>
    </section>

    <section id="coaching-cancellation">
      <h2>2. Cancellation of sessions</h2>
      <p>To cancel without incurring a charge, we require at least 24-hours notice. Sessions being cancelled within 24-hours of the appointment time will be liable to a late cancellation charge. The charge will be the full fee of the session being cancelled. Any rearranged session will be at a time mutually agreed by you and Performance &amp; Health Therapy Ltd.</p>
    </section>

    <section id="coaching-lateness">
      <h2>3. Lateness</h2>
      <p>If you are late for your session your session will be reduced in accordance with that time. If you are more than 30 minutes late Performance &amp; Health Therapy Ltd are entitled to cancel your session, in which case no refund will be given.</p>
    </section>

    <section id="coaching-expiry">
      <h2>4. Expiry and extensions</h2>
      <p>You can complete your sessions at your own pace however we strongly advise completing at a rate of two sessions per week. This is to ensure that your sessions will be delivered to you giving you the best opportunity to achieve your goals.</p>
      <p>Programmes may be extended at the discretion of Performance &amp; Health Therapy Ltd or for the following reasons:</p>
      <ul>
        <li>Serious illness or injury</li>
        <li>Pregnancy</li>
        <li>Redundancy</li>
      </ul>
    </section>

    <section id="coaching-ep">
      <h2>5. Your Exercise Physiologist</h2>
      <p>An Exercise Physiologist will lead your programme. If at any time you are unhappy with your Exercise Physiologist you may transfer to another Exercise Physiologist provided that there is another Exercise Physiologist available. In all circumstances your previous sessions will remain used. Your Exercise Physiologist cannot at any time prescribe a course of medical treatment or diagnose any medical condition. They may at any time ask to speak to your healthcare professional or practitioner.</p>
    </section>

    <section id="coaching-refunds">
      <h2>6. Refunds</h2>
      <p>Refunds will be given at the discretion of Performance &amp; Health Therapy Ltd and will be reviewed on an individual basis. If your Exercise Physiologist leaves Performance &amp; Health Therapy Ltd you will not be entitled to a refund and you will be transferred to another Exercise Physiologist.</p>
    </section>

    <section id="coaching-privacy">
      <h2>7. Our commitment to your privacy</h2>
      <p>The confidentiality of your personal information is of paramount importance to Performance &amp; Health Therapy Ltd. We have a Privacy Policy to explain how we collect, store and handle your personal data. You can see our privacy policy <Link to="/privacy">here</Link>.</p>
    </section>

    <section id="coaching-health">
      <h2>8. Health and Safety</h2>
      <p><strong>Physical Condition:</strong> By participating in our exercise sessions, you acknowledge that you are in adequate physical condition to participate safely. You are responsible for consulting with your doctor if you have any concerns about your fitness level or any pre-existing medical conditions.</p>
      <p><strong>Disclosure of Medical Conditions:</strong> You agree to inform the instructor of any relevant medical conditions, injuries, or limitations prior to the start of each class.</p>
      <p><strong>Following Instructions:</strong> You agree to follow the instructions provided by the instructor during the class. If you feel any pain or discomfort, you should stop immediately and inform the instructor.</p>
      <p><strong>Personal Belongings:</strong> We are not responsible for any loss or damage to your personal belongings brought to the class.</p>
    </section>

    <section id="coaching-conduct">
      <h2>9. Conduct</h2>
      <p>Participants are expected to conduct themselves in a respectful and appropriate manner towards instructors and other participants. We reserve the right to refuse participation to anyone whose conduct is deemed disruptive or inappropriate.</p>
    </section>

    <section id="coaching-ip">
      <h2>10. Intellectual Property</h2>
      <p>Any choreography, music, or other materials provided during the classes are the intellectual property of PH Therapy Ltd and may not be copied or distributed without permission.</p>
    </section>

    <section id="coaching-liability">
      <h2>11. Limitation of Liability</h2>
      <p>To the maximum extent permitted by law, PH Therapy Ltd, its instructors, exercise physiologists and employees shall not be liable for any injury, loss, or damage to personal property arising out of or in connection with your participation in our exercise sessions.</p>
    </section>

    <section id="coaching-changes">
      <h2>12. Changes to Terms of Service</h2>
      <p>We reserve the right to modify these Terms of Service at any time. Any changes will be posted on our website and will be effective immediately upon posting. Your continued participation in our classes after any such changes constitutes your acceptance of the new Terms of Service.</p>
    </section>

    <section id="coaching-governing">
      <h2>13. Governing Law</h2>
      <p>These Terms of Service shall be governed by and construed in accordance with the laws of England and Wales.</p>
    </section>

    <section id="coaching-contact">
      <h2>14. Contact Us</h2>
      <p>If you have any questions or concerns about these Terms of Service, please contact us at <a href="mailto:info@pandhtherapy.co.uk">info@pandhtherapy.co.uk</a>.</p>
    </section>

    <section id="coaching-informed-consent">
      <h2>Informed consent</h2>
      <p>By participating in our exercise sessions, you confirm that:</p>
      <ul>
        <li>You agree to inform Performance &amp; Health Therapy Ltd of any conditions, medical or otherwise, that may affect your ability to participate in exercise sessions.</li>
        <li>You agree to disclose any changes to your medication or treatment plan.</li>
        <li>You agree to disclose whenever suggested activities cause distress or discomfort beyond your threshold.</li>
        <li>You agree to disclose any new, unusual or worsened symptoms that you experience before, during or after exercise as soon as you become aware of them.</li>
        <li>You agree that Performance &amp; Health Therapy Ltd can request, and be granted access to, relevant medical information held by your healthcare professional or practitioner.</li>
        <li>You agree that Performance &amp; Health Therapy Ltd can contact your healthcare professional or practitioner with updates during the course of your programme and if there should be any significant change in your condition or symptoms.</li>
        <li>You understand that participating in physical activity and exercise can carry a risk, and you accept all responsibility for that risk. You understand that due care will be undertaken at all times.</li>
        <li>You understand and accept that physical contact may be required in relation to assessment, treatment, and rehabilitation. This does not affect your basic rights.</li>
        <li>You agree that PH Therapy may share updates with your consultant as and when needed.</li>
      </ul>
    </section>

    <section id="coaching-marketing">
      <h2>Marketing communications</h2>
      <p>As part of your registration, you may be asked about your preferences for receiving marketing communications from PH Therapy Ltd, including our email newsletter.</p>
      <p>If you consent to receive our newsletter, we will use your email address to send you updates and promotional material. You have the right to change your marketing preferences or unsubscribe from our newsletter at any time. You can do this by using the &quot;unsubscribe&quot; link found in our email communications or by contacting us directly at <a href="mailto:info@pandhtherapy.co.uk">info@pandhtherapy.co.uk</a>.</p>
    </section>
  </LegalPageTemplate>
);

export default TermsPage;
